import { useState, useCallback, useMemo } from 'react'

// Reusable bulk-selection helper for tables that support "select all + delete".
// `ids` is the array of currently-visible row ids.
export default function useBulkSelection(ids = []) {
  const [selected, setSelected] = useState(() => new Set())

  const toggle = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  const allSelected = ids.length > 0 && ids.every(id => selected.has(id))
  const someSelected = ids.some(id => selected.has(id)) && !allSelected

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const everySelected = ids.length > 0 && ids.every(id => prev.has(id))
      return everySelected ? new Set() : new Set(ids)
    })
  }, [ids])

  // Only count ids still visible (so filtering doesn't strand selections)
  const selectedIds = useMemo(() => ids.filter(id => selected.has(id)), [ids, selected])

  return { selected, selectedIds, isSelected: (id) => selected.has(id), toggle, toggleAll, clear, allSelected, someSelected }
}

// Delete each id via `deleteOne(id)` (a function returning a promise).
// Confirms once, continues past failures, returns { ok, failed }.
export async function bulkDelete(ids, deleteOne) {
  let ok = 0
  let failed = 0
  for (const id of ids) {
    try {
      await deleteOne(id)
      ok++
    } catch (e) {
      failed++
    }
  }
  return { ok, failed }
}
